// Everything needed to set up and launch the four social pages. Copy is written to
// each platform's limits; dates are the November 15, 2026 launch calendar.
export const HANDLE = "hearthvault";
export const LAUNCH_DATE = "2026-11-15";

const link = (source, content) =>
  `https://hearthvault.family/welcome?utm_source=${source}&utm_medium=social&utm_campaign=launch-nov26&utm_content=${content}`;

const COMMON = {
  name: "HearthVault",
  tagline: "The family document safe.",
  oneLiner: "HearthVault files your family's wills, policies, receipts and passwords, reminds you before dates lapse, and hands the right bundle to the right person.",
  longAbout: `HearthVault is the family document safe.

Drop in a receipt, a policy or a will — upload it, snap it with your phone, or forward the email. It comes back named, dated and filed in the right folder, with a confidence badge so you stay in charge. One tap re-files it.

Then it quietly works for you: renewal reminders before anything lapses, a tax pack when you need it, a monthly digest, and executor access for the one person who'll need to know where everything is.

Built for parents sorting the shoebox, for the daughter who manages Mom's papers from two states away, and for the advisors and attorneys who want the plan they built to stay current.

Encrypted at rest. Free to start. No card needed.

Launching 15 November 2026 — hearthvault.family`,
  hashtags: ["#HearthVault", "#FamilyDocuments", "#EstatePlanning", "#LifeAdmin", "#Caregiving", "#GetOrganized"],
};

export const KIT = {
  facebook: {
    page: {
      "Page name": COMMON.name,
      "Username": `@${HANDLE}`,
      "Category": "Software company · Financial service",
      "Intro (101 chars max)": "The family document safe. Wills, policies, receipts — filed for you, reminded before anything lapses.",
      "About (long)": COMMON.longAbout,
      "Website": link("facebook", "page-about"),
      "Action button": "Sign up → " + link("facebook", "page-button"),
      "Pinned post caption": `We're HearthVault — the family document safe. 🏠

If your family's papers live in a shoebox, a drawer and three email inboxes, this page is for you. Here you'll find:
• The kitchen-table checklist (free, printable)
• Short walkthroughs of how the vault works
• Honest notes on caring for a parent's paperwork

Start here → ${link("facebook", "pinned")}`,
    },
    assets: [
      { label: "Profile picture", file: "profile-terracotta.jpg", dims: "1024×1024 · shows as a circle", use: "Page profile photo" },
      { label: "Cover photo", file: "cover-facebook-1640x624.jpg", dims: "1640×624 (displays 820×312)", use: "Page cover. Text sits in the mobile-safe centre." },
    ],
    posts: [
      { date: "2026-11-15", title: "Launch", image: "post-launch-1200x630.jpg", story: "story-launch-1080x1920.jpg", caption: `Today HearthVault opens its doors.

One family vault for the papers that matter — wills, policies, receipts, passwords. Drop them in; they come back named, dated and filed. Then the reminders start, before anything lapses.

Free to start. About two minutes to set up. No card needed.

→ ${link("facebook", "launch")}`, tags: ["#HearthVault", "#FamilyDocuments", "#LaunchDay"] },
      { date: "2026-11-17", title: "The kitchen-table checklist", image: "post-checklist-1200x630.jpg", story: "story-checklist-1080x1920.jpg", caption: `Print this, pour the tea, go through it together.

Six short sections — identity, money, insurance, home, digital life, people to call. Tick what you can find. Circle what you can't.

The circles are the plan.

Free printable → ${link("facebook", "checklist")}`, tags: ["#FamilyDocuments", "#GetOrganized", "#Caregiving"] },
      { date: "2026-11-19", title: "Caring for a parent", image: "post-caregiver-1200x630.jpg", story: "story-caregiver-1080x1920.jpg", caption: `"Where does Mom keep the insurance papers?"

If that question lands on you — from two states away, between school pickups — HearthVault gives you one folder you both can open. Her policies, accounts and wishes, with reminders that go to whoever's on duty that week.

→ ${link("facebook", "caregiver")}`, tags: ["#Caregiving", "#AgingParents", "#HearthVault"] },
      { date: "2026-11-22", title: "Watch the 60-second tour", image: "post-tour-1200x630.jpg", story: "story-tour-1080x1920.jpg", caption: `Three papers go in. Named, dated and filed come out. Then the reminder fires.

Watch the whole thing in about a minute.

▶ ${link("facebook", "tour")}`, tags: ["#HearthVault", "#LifeAdmin"] },
      { date: "2026-11-26", title: "Thanksgiving: one conversation", image: "post-executor-1080.jpg", story: "story-executor-1080x1920.jpg", caption: `This week, while everyone's at the same table: one quiet conversation.

Not about the will — just about where things are. Who'd know which bank, which drawer, which password manager?

HearthVault's executor access answers that question once, for good.

→ ${link("facebook", "thanksgiving")}`, tags: ["#Thanksgiving", "#FamilyDocuments", "#EstatePlanning"] },
      { date: "2026-11-29", title: "Before it lapses", image: "post-reminder-1080.jpg", story: "story-reminder-1080x1920.jpg", caption: `The home insurance renews in three weeks. Did anyone notice?

HearthVault reads the dates on your documents and nudges you before — not after. Passports, car registration, policies, warranties.

→ ${link("facebook", "reminders")}`, tags: ["#LifeAdmin", "#HearthVault"] },
    ],
  },

  youtube: {
    page: {
      "Channel name": COMMON.name,
      "Handle": `@${HANDLE}`,
      "Channel description": `${COMMON.oneLiner}

On this channel: short walkthroughs of the vault, the 60-second tour, and honest talks about sorting the family shoebox — for parents, caregivers, and the advisors who help them.

New tour every other week.

Start free → ${link("youtube", "channel-about")}`,
      "Channel links": `Website → ${link("youtube", "channel-link")}\nChecklist → https://hearthvault.family/checklist`,
      "Channel keywords": "family documents, estate planning, document organizer, life admin, caregiving, wills, insurance reminders, HearthVault",
      "Video title (tour)": "HearthVault in 60 seconds — shoebox to sorted",
      "Video description (tour)": `Three papers go in. Named, dated and filed come out. Then the reminder fires — before the policy lapses.

HearthVault is the family document safe. Free to start, about two minutes to set up, no card needed.
→ ${link("youtube", "tour-description")}

Chapters
0:00 Drop it in
0:20 Filed and named
0:45 Reminded

Free kitchen-table checklist → https://hearthvault.family/checklist

#HearthVault #FamilyDocuments #LifeAdmin`,
      "Video tags (tour)": "HearthVault, family document safe, document organizer app, estate planning for families, how to organize family documents, caregiving paperwork, insurance renewal reminder",
    },
    assets: [
      { label: "Profile picture", file: "profile-terracotta.jpg", dims: "1024×1024 · shows as a circle", use: "Channel profile picture" },
      { label: "Channel banner", file: "banner-youtube-2560x1440.jpg", dims: "2560×1440 · text in the 1546×423 safe area", use: "Channel art (shows correctly on TV, desktop and mobile)" },
      { label: "Tour thumbnail", file: "thumb-tour-1280x720.jpg", dims: "1280×720", use: "Custom thumbnail for the 60-second tour" },
    ],
    posts: [
      { date: "2026-11-15", title: "Publish: the 60-second tour", image: "thumb-tour-1280x720.jpg", story: "story-tour-1080x1920.jpg", caption: `Start free, no card needed → ${link("youtube", "tour-pinned-comment")} — and grab the kitchen-table checklist at hearthvault.family/checklist`, note: "Upload the tour using the 'Video title (tour)' and 'Video description (tour)' fields above, set the custom thumbnail, add the three chapters, then pin this as the first comment.", tags: [] },
      { date: "2026-11-15", title: "Community post: we're live", image: "post-launch-1080.jpg", story: "story-launch-1080x1920.jpg", caption: `HearthVault is live. One family vault for the papers that matter — filed for you, reminded before anything lapses.

The 60-second tour is up. Free to start → ${link("youtube", "community-launch")}`, tags: [] },
      { date: "2026-11-22", title: "Short: Drop it in (0:00–0:20)", image: "post-ritual-1080.jpg", story: "story-ritual-1080x1920.jpg", caption: `Snap it. Done. — HearthVault

One paper, one tap. HearthVault names, dates and files it. Full tour on the channel. → ${link("youtube", "short-drop")}`, note: "Cut chapter 1 of the tour (0:00–0:20) as a vertical Short. First line is the title, the rest is the description.", tags: ["#Shorts", "#HearthVault", "#LifeAdmin"] },
      { date: "2026-11-29", title: "Short: Reminded (0:45–1:00)", image: "post-reminder-1080.jpg", story: "story-reminder-1080x1920.jpg", caption: `The reminder arrives three weeks early — HearthVault

Before it lapses, not after. → ${link("youtube", "short-remind")}`, note: "Cut chapter 3 of the tour (0:45–1:00) as a vertical Short. First line is the title, the rest is the description.", tags: ["#Shorts", "#HearthVault", "#Reminders"] },
    ],
  },

  instagram: {
    page: {
      "Name": COMMON.name,
      "Username": HANDLE,
      "Category": "Software",
      "Bio (150 chars max)": "The family document safe 🏠\nWills · policies · receipts — filed for you\nReminded before anything lapses\nLaunching 15 Nov ↓",
      "Link in bio": link("instagram", "bio"),
      "Highlights (in order)": "Launch · Checklists · The tour · Rituals · Advisors",
      "Pinned post caption": `One paper. One drawer. One tap.

HearthVault is the family document safe — drop in the envelope on the counter and it comes back named, dated and filed. Then the reminders start, before anything lapses.

Small rituals live here. The vault lives at the link in bio.`,
    },
    assets: [
      { label: "Profile picture", file: "profile-terracotta.jpg", dims: "1024×1024 · shows as a circle", use: "Profile photo" },
      { label: "Highlight: Launch", file: "highlight-launch-1080.jpg", dims: "1080×1080", use: "Story highlight cover" },
      { label: "Highlight: Checklists", file: "highlight-checklists-1080.jpg", dims: "1080×1080", use: "Story highlight cover" },
      { label: "Highlight: The tour", file: "highlight-the-tour-1080.jpg", dims: "1080×1080", use: "Story highlight cover" },
      { label: "Highlight: Rituals", file: "highlight-rituals-1080.jpg", dims: "1080×1080", use: "Story highlight cover" },
      { label: "Highlight: Advisors", file: "highlight-advisors-1080.jpg", dims: "1080×1080", use: "Story highlight cover" },
    ],
    posts: [
      { date: "2026-11-15", title: "Launch", image: "post-launch-1080.jpg", story: "story-launch-1080x1920.jpg", caption: `We're open. 🏠

One family vault. Four quiet doorways in.

HearthVault files the papers that matter — wills, policies, receipts, passwords — and reminds you before anything lapses. Free to start. Link in bio.`, tags: ["#HearthVault", "#FamilyDocuments", "#LifeAdmin", "#GetOrganized", "#LaunchDay", "#HomeOrganization", "#AdultingTips"] },
      { date: "2026-11-16", title: "Sunday ritual", image: "post-ritual-1080.jpg", story: "story-ritual-1080x1920.jpg", caption: `Sunday, 7pm. The oldest envelope on the counter.

Snap it. HearthVault names it, dates it, files it. Five minutes, one paper, one less thing.

Save this for next Sunday. 🔖`, tags: ["#SundayReset", "#LifeAdmin", "#HomeOrganization", "#HearthVault", "#SmallHabits"] },
      { date: "2026-11-18", title: "Checklist", image: "post-checklist-1080.jpg", story: "story-checklist-1080x1920.jpg", caption: `Tick what you have. Circle what you can't find.

The kitchen-table checklist — six short sections, 31 papers worth finding. The circles are the plan.

Free printable at the link in bio. 📄`, tags: ["#Checklist", "#FamilyDocuments", "#GetOrganized", "#HearthVault", "#Printable"] },
      { date: "2026-11-21", title: "Tour (Reel)", image: "post-tour-1080.jpg", story: "story-tour-1080x1920.jpg", caption: `Shoebox to sorted, in about a minute. ▶

Three papers go in. Named, dated, filed. Then the reminder fires.

Full tour at the link in bio.`, note: "Post the 60-second tour video as a Reel with this caption.", tags: ["#HearthVault", "#BeforeAndAfter", "#LifeAdmin", "#Reels", "#Organized"] },
      { date: "2026-11-24", title: "Caring for a parent", image: "post-caregiver-1080.jpg", story: "story-caregiver-1080x1920.jpg", caption: `"Where does Mom keep the insurance papers?"

One folder both of you can open. Her policies, accounts and wishes — with reminders that go to whoever's on duty that week.

For every daughter managing it from two states away. 💛`, tags: ["#Caregiving", "#AgingParents", "#SandwichGeneration", "#HearthVault", "#FamilyFirst"] },
      { date: "2026-11-28", title: "Reminder", image: "post-reminder-1080.jpg", story: "story-reminder-1080x1920.jpg", caption: `The reminder arrives three weeks early.

Home insurance, passports, the car registration — HearthVault reads the dates and nudges you before, not after.

Quiet, on time, done. Link in bio.`, tags: ["#LifeAdmin", "#HearthVault", "#Adulting", "#Reminders", "#PeaceOfMind"] },
    ],
  },

  linkedin: {
    page: {
      "Page name": COMMON.name,
      "Public URL": `linkedin.com/company/${HANDLE}`,
      "Tagline (120 chars max)": "The family document safe. Keep the plan you built current — for families, advisors and attorneys.",
      "Industry · Size · Type": "Software Development · 2–10 employees · Privately held",
      "About (2,000 chars max)": `HearthVault is the family document safe.

Families drop in wills, policies, receipts and passwords — by upload, phone camera or forwarded email. Each document comes back named, categorized and dated with a confidence badge; one tap re-files it. Then HearthVault works quietly in the background: renewal reminders before anything lapses, tax packs on demand, a monthly digest, and executor access for the person who will one day need to know where everything is.

For advisors, attorneys and planners, HearthVault solves the problem that follows every well-built plan: it goes stale. Clients keep documents current between reviews, and your portal shows what's missing before the meeting — not after.

Encrypted at rest. Free for families to start.

Launching 15 November 2026. Learn more at hearthvault.family.`,
      "Website": link("linkedin", "page-about"),
      "Custom button": "Learn more → " + link("linkedin", "page-button"),
      "Specialties": "Family document management, estate planning readiness, executor access, document reminders, advisor client portals, secure family vault",
    },
    assets: [
      { label: "Logo", file: "profile-terracotta.jpg", dims: "1024×1024 (LinkedIn asks for 300×300 min)", use: "Company page logo" },
      { label: "Cover image", file: "cover-linkedin-2256x382.jpg", dims: "2256×382 (displays 1128×191)", use: "Company page cover" },
    ],
    posts: [
      { date: "2026-11-16", title: "Launch (Monday)", image: "post-launch-1200x630.jpg", story: "story-launch-1080x1920.jpg", caption: `Today we're launching HearthVault — the family document safe.

Every advisor has watched a well-built plan go stale. The will is signed, the policies are in place, and then life happens: a new car, a refinance, a move. The documents land in a drawer and the plan drifts.

HearthVault keeps it current. Families drop in documents as they arrive; each comes back named, dated and filed. Renewal reminders fire before anything lapses. And the one person who'll need to know where everything is has read-only access long before the day.

Free for families to start. A portal for the professionals who help them.

→ ${link("linkedin", "launch")}`, tags: ["#EstatePlanning", "#FinancialPlanning", "#WealthManagement", "#HearthVault"] },
      { date: "2026-11-19", title: "Keep the plan current", image: "post-advisor-1200x630.jpg", story: "story-advisor-1080x1920.jpg", caption: `The annual review shouldn't start with "what changed?"

With HearthVault, clients drop in documents between meetings. Your portal shows what's new, what's expiring, and what's missing — before you sit down.

The plan you built stays the plan they follow.

→ ${link("linkedin", "advisor-portal")}`, tags: ["#FinancialAdvisors", "#EstatePlanning", "#ClientExperience", "#HearthVault"] },
      { date: "2026-11-23", title: "Executor readiness", image: "post-executor-1080.jpg", story: "story-executor-1080x1920.jpg", caption: `Being named executor is an honour. Being an executor who can't find the accounts is a six-month ordeal.

HearthVault's executor access is a read-only profile: the will, the accounts, the plan — ready before it's ever needed. No scavenger hunt, no guessing which bank.

A small thing to set up now. A large thing to have later.

→ ${link("linkedin", "executor")}`, tags: ["#EstatePlanning", "#Executor", "#TrustAndEstates", "#HearthVault"] },
      { date: "2026-11-30", title: "Caregiving at work", image: "post-caregiver-1200x630.jpg", story: "story-caregiver-1080x1920.jpg", caption: `One in five employees is also caring for an aging parent — often from another state, often between meetings.

Much of that work is paperwork. HearthVault gives families one shared vault for a parent's policies, accounts and wishes, with reminders that go to whoever is on duty that week.

Quieter paperwork is a benefit too.

→ ${link("linkedin", "caregiving")}`, tags: ["#Caregiving", "#SandwichGeneration", "#EmployeeBenefits", "#HearthVault"] },
    ],
  },
};

export const KIT_ORDER = ["facebook", "youtube", "instagram", "linkedin"];

// Pre-launch countdown, 8–14 Nov 2026. Same series on every network; links carry the network's utm_source.
const TEASER_COPY = [
  { n: 7, date: "2026-11-08", title: "7 days — the shoebox gets a home", caption: `In seven days, the shoebox gets a home.

HearthVault is the family document safe. Wills, policies, receipts, passwords — dropped in as they are, filed as they should be, reminded before anything lapses.

15 November. Follow along this week — we'll show you what's inside.` },
  { n: 6, date: "2026-11-09", title: "6 days — where is the will?", caption: `Quick question: where is the will, right now?

Not "somewhere safe." Which drawer, which folder, which lawyer's office? If you paused, you're in good company — most families do.

In six days, that question gets a one-tap answer.` },
  { n: 5, date: "2026-11-10", title: "5 days — three papers everyone loses", caption: `Three papers everyone loses:
1. The car title
2. The home insurance declarations page
3. The one receipt the warranty needs

Five days until they get a home that remembers for you.` },
  { n: 4, date: "2026-11-11", title: "4 days — who would know which bank?", caption: `If something happened tomorrow, who would know which bank? Which password manager? Where the policy lives?

Being the person who knows shouldn't be a scavenger hunt. In four days: executor access, built in.` },
  { n: 3, date: "2026-11-12", title: "3 days — the 60-second tour", caption: `Sunday we'll show you the whole thing in about a minute.

Three papers go in. Named, dated and filed come out. Then the reminder fires — before the policy lapses.

Three days.` },
  { n: 2, date: "2026-11-13", title: "2 days — the checklist", caption: `The kitchen-table checklist is almost ready.

Six short sections. Thirty-one papers worth finding. Tick what you have, circle what you can't find — the circles are the plan.

Free, printable, two days away.` },
  { n: 1, date: "2026-11-14", title: "1 day — tomorrow", caption: `Tomorrow.

One family vault. Four quiet doorways in. Free to start, about two minutes to set up, no card needed.

See you at 9am.` },
];
const TEASER_TAGS = { facebook: ["#HearthVault", "#ComingSoon", "#FamilyDocuments"], youtube: ["#HearthVault", "#ComingSoon"], instagram: ["#HearthVault", "#ComingSoon", "#LifeAdmin", "#FamilyDocuments", "#Countdown"], linkedin: ["#HearthVault", "#EstatePlanning", "#ComingSoon"] };

export const teasersFor = (network) =>
  TEASER_COPY.map((t) => ({
    date: t.date,
    title: t.title,
    image: `teaser-${t.n}-1080.jpg`,
    story: `teaser-${t.n}-1080x1920.jpg`,
    caption: `${t.caption}\n\n→ ${link(network, `teaser-${t.n}`)}`,
    tags: TEASER_TAGS[network],
    note: network === "youtube" ? "Post as a Community post (image + caption)." : undefined,
  }));
